import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Button, Typography } from '@mui/material';
import { Address } from '../../types/Address';
import { PREFERENCES_VALID_KEYS, Preference } from '../../types/Preference';
import { useGetAllPreferences } from '../../services/react-query/preferences';
import AddressForm, { BLANK_ADDRESS, REQUIRED_FIELDS } from '../common/Address/AddressForm';
import _ from 'lodash';
import PreferencePicker from '../preferences/picker/PreferencePicker';

interface AccountAddressModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (address: Address) => void;
  addressToEdit: Address | null;
}

const AccountAddressModal: React.FC<AccountAddressModalProps> = ({ isOpen, onCancel, onSave, addressToEdit }) => {
  const [address, setAddress] = useState<Address>(BLANK_ADDRESS);

  // Parent fetches options and passes them to PreferencePicker
  const { data: preferencesData = [], isFetching: isPrefsFetching } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.ADDRESS_TYPE] } },
  });

  // Options for the picker (already filtered by key)
  const options: Preference[] = preferencesData;

  useEffect(() => {
    if (addressToEdit) setAddress(addressToEdit);
    else setAddress(BLANK_ADDRESS);
  }, [isOpen, addressToEdit]);

  const handleTypeChange = (value: Preference | null | undefined) => {
    const pref = value ?? null;
    setAddress((prev) => ({
      ...prev,
      type_uuid: pref?.uuid || '',
      type_value: pref?.value || '',
    }));
  };
  const isFormInvalid = REQUIRED_FIELDS.some((field) => !_.trim((address as any)[field]));

  const handleSubmit = () => {
    if (!isFormInvalid) {
      onSave(address);
      onCancel();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography variant="h6" sx={{ m: 2 }}>
        {addressToEdit ? 'Edit Address' : 'Add Address'}
      </Typography>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PreferencePicker
          label="Address Type"
          options={options}
          loading={isPrefsFetching}
          value={options.find((p) => p.uuid === address.type_uuid) || null}
          onValueChange={handleTypeChange}
        />

        <AddressForm address={address} setAddress={setAddress} />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isFormInvalid}>
          {addressToEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountAddressModal;
